import { Abortable } from '../../lib/abortable.js';
import { isStringEmpty } from '../../lib/utils.js';
import { KvScanner } from './kv-scanner.js';
import { matchKvPattern } from './kv-utils.js';

/**
 * @import { CcKvExplorer } from './cc-kv-explorer.js'
 * @import { CcKvExplorerState, CcKvExplorerStateLoaded, CcKvKey, CcKvKeyType, CcKvKeyState } from './cc-kv-explorer.types.js'
 * @import { KvClient } from './kv-client.js'
 * @import { ObjectOrFunction } from '../common.types.js'
 */

export class KvKeysCtrl {
  /**
   * @param {CcKvExplorer} component
   * @param {(stateUpdater: ObjectOrFunction<CcKvExplorerState>) => void} updateState
   * @param {KvClient} kvClient
   */
  constructor(component, updateState, kvClient) {
    this._component = component;
    this._updateState = updateState;
    this._kvClient = kvClient;
    this._abortable = new Abortable();
    this._scanner = new KvKeysScanner(kvClient, this._abortable, () => {
      const state = this._getViewState();

      if (state.type !== 'loaded') {
        return;
      }

      this._updateState({
        type: state.type,
        keys: this._scanner.elements,
        total: this._scanner.total,
      });
    });

    /** @type {CcKvKey} */
    this._selectedKey = null;
  }

  abort() {
    this._abortable.abort();
  }

  /**
   * @param {CcKvKeyType|('all')} type
   * @param {string} pattern
   */
  async filter(type, pattern) {
    if (this._getViewState().type !== 'loaded' && this._getViewState().type !== 'error-keys') {
      return;
    }

    const filter = {
      type: type === 'all' ? null : type,
      pattern: isStringEmpty(pattern) ? null : pattern,
    };

    this._scanner.setFilter(filter);
    await this.fetchKeys({ type: 'filtering', keys: [] });
  }

  async refresh() {
    if (this._getViewState().type !== 'loaded' && this._getViewState().type !== 'error-keys') {
      return;
    }

    this._scanner.reset();
    await this.fetchKeys({ type: 'refreshing', keys: [] });
  }

  async loadMore() {
    if (this._getViewState().type !== 'loaded') {
      return;
    }

    await this.fetchKeys();
  }

  /**
   * @param {CcKvKey} key
   * @return {boolean}
   */
  select(key) {
    if (this._getViewState().type !== 'loaded') {
      return false;
    }

    this._updateKeyState(key.name, 'selected');

    return true;
  }

  clearSelection() {
    if (this._selectedKey != null) {
      this._updateKeyState(this._selectedKey.name, 'idle');
    }
  }

  /**
   * @param {string} keyName
   */
  async delete(keyName) {
    const state = this._getViewState();

    if (state.type !== 'loaded') {
      return;
    }

    const originalKeyState = this._scanner.getElement(keyName);

    this._updateKeyState(keyName, 'deleting');

    try {
      await this._kvClient.deleteKey(keyName);
      const deletedKeyIndex = this._scanner.delete(keyName);
      if (this._selectedKey?.name === keyName) {
        this._selectedKey = null;
      }

      await this._component.focusAfterDeleteKeyAt(deletedKeyIndex);
    } catch (e) {
      this._updateKeyState(keyName, originalKeyState.type);
      throw e;
    }
  }

  /**
   * @param {CcKvKey} key
   */
  add(key) {
    const state = this._getViewState();

    if (!this._isLoadedState(state)) {
      return;
    }

    this.clearSelection();

    /** @type {CcKvKeyState} */
    const keyState = { type: 'selected', key };
    this._scanner.update([keyState]);
    this._selectedKey = key;
  }

  /**
   * @param {{type?: 'loading-keys'|'filtering'|'refreshing', keys?: Array<CcKvKeyState>, total?: number}} [init]
   */
  async fetchKeys(init = {}) {
    if (this._scanner.hasMore()) {
      /** @type {Array<CcKvKeyState>} */
      let keys;
      /** @type {number} */
      let total;

      const state = this._getViewState();

      if (state.type === 'loaded' || state.type === 'loading-keys') {
        keys = init.keys ?? state.keys;
        total = init.total ?? state.total;
      }

      this._updateState({ type: init.type ?? 'loading-keys', keys: keys ?? [], total: total ?? 0 });

      await this._scanner.loadMore();
      keys = this._scanner.elements;
      total = this._scanner.total;

      if (this._selectedKey != null) {
        const isSelectedKeyAbsent = this._scanner.getElement(this._selectedKey.name) == null;

        if (isSelectedKeyAbsent) {
          this._selectedKey = null;
        }
      }

      if (this._selectedKey != null) {
        const keyNameToSelect = this._selectedKey.name;
        this._selectedKey = null;
        this._updateKeyState(keyNameToSelect, 'selected');
      }

      this._updateState({ type: 'loaded', keys, total });
    }
  }

  /**
   * @param {string} keyName
   */
  onKeyNotFound(keyName) {
    this._updateKeyState(keyName, 'idle');
  }

  _getViewState() {
    return this._component.state;
  }

  /**
   * @param {string} key
   * @param {CcKvKeyState['type']} newState
   */
  _updateKeyState(key, newState) {
    const keyStateToUpdate = this._scanner.getElement(key);

    if (keyStateToUpdate != null) {
      /** @type {CcKvKeyState} */
      const updatedKeyState = { ...keyStateToUpdate, type: newState };

      const toUpdate = [updatedKeyState];

      // deselection
      if (newState !== 'selected' && key === this._selectedKey?.name) {
        this._selectedKey = null;
      }

      // new selection
      if (newState === 'selected' && this._selectedKey != null && key !== this._selectedKey.name) {
        toUpdate.push({ type: 'idle', key: this._selectedKey });
      }

      if (newState === 'selected') {
        this._selectedKey = keyStateToUpdate.key;
      }

      this._scanner.update(toUpdate);
    }
  }

  /**
   *
   * @param {CcKvExplorerState} state
   * @return {state is CcKvExplorerStateLoaded}
   */
  _isLoadedState(state) {
    return state.type === 'loaded' || state.type === 'loading-keys';
  }
}

/**
 * @extends {KvScanner<CcKvKeyState, {type: CcKvKeyType, pattern: string}>}
 */
class KvKeysScanner extends KvScanner {
  /**
   * @param {KvClient} kvClient
   * @param {Abortable} abortable
   * @param {function} onChange
   */
  constructor(kvClient, abortable, onChange) {
    super(abortable, onChange);
    this._kvClient = kvClient;
  }

  /**
   * @param {CcKvKeyState} item
   * @returns {string}
   */
  getId(item) {
    return item.key.name;
  }

  /**
   * @param {CcKvKeyState} item
   * @returns {boolean}
   */
  matchFilter(item) {
    return (
      (this._filter?.type == null || item.key.type === this._filter.type) &&
      (isStringEmpty(this._filter?.pattern) || matchKvPattern(this._filter.pattern, item.key.name))
    );
  }

  /**
   * @param {number} count
   * @param {AbortSignal} [_signal]
   * @return {Promise<{cursor: number, total: number, elements: Array<CcKvKeyState>}>}
   */
  async fetch(count, _signal) {
    const r = await this._kvClient.scanKeys({
      cursor: this._cursor,
      count,
      type: this._filter?.type,
      match: this._filter?.pattern,
    });
    return {
      cursor: r.cursor,
      total: r.total,
      elements: r.keys.map((key) => ({ type: 'idle', key })),
    };
  }
}
